// Daten der Objektseite (E3). Alle Bereiche parallel; jeder Bereich lädt und
// scheitert für sich (ein Fehler bei Zählern blockiert nicht die Einheiten).
// Nach Änderungen: neuLaden('notizen') usw. – das Mietkonto kommt in E4 dazu.

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase/client';
import { aktuelleJeEinheit, type EinheitEigZeitraum } from '../eigentuemer/einheitEigentum';
import { heuteIso } from './format';
import { ladeObjektKontakte, type ObjektKontaktZeile } from '../objekte/objektKontakte';
import { ladeDienstleisterFuerObjekt } from '../dienstleister/api';
import type { EinheitDetailRoh, MieterDetailRoh } from './objektDetail';
import type { ObjektListeRoh } from './portfolio';
import type { ZustaendigOption } from './useObjektListe';

export interface ObjektRoh extends ObjektListeRoh {
  baujahr?: number | string | null;
  heizungsart?: string | null;
  energieausweis?: string | null;
  energieklasse?: string | null;
  letzte_pruefung?: string | null;
  ausstattung?: string | null;
  stellplatz_typ?: string | null;
  stellplatz_anzahl?: number | null;
  onedrive_link?: string | null;
  miete_soll?: number | string | null;
  notizen?: string | null;
  etage?: string | null;
}

export interface HausRoh { id: string; bezeichnung: string | null; strasse: string | null; hausnummer: string | null; plz: string | null; stadt: string | null }
export interface EigentuemerZeile { id: string; vorname: string | null; nachname: string | null; firma_name: string | null; email: string | null; telefon: string | null }
export interface WegZeile { einheit_id: string; eigentuemer_id: string | null; mea: number | null; hausgeld_vorschuss: number | null; ruecklage_vorschuss: number | null; sev_status: string | null; name: string }
export interface NotizZeile { id: string; text: string; created_at: string }
export interface AliasZeile { id: string; alias: string; quelle: 'manuell' | 'gelernt' }
export interface TerminZeile { id: string; titel: string | null; typ: string | null; typ_freitext?: string | null; start_datum: string; end_datum: string | null; ganztaegig?: boolean | null; status: string | null; ort?: string | null; einheit_id?: string | null }
export interface ZaehlerZeile { id: string; zaehler_nummer: string | null; typ: string | null; einheit: string | null; standort: string | null; einheit_bezeichnung: string | null; aktiv: boolean | null; haus_id: string | null }
export interface DokumentZeile { id: string; titel: string | null; datei_name: string | null; datei_typ: string | null; datei_url: string | null; onedrive_url: string | null; gdrive_file_id: string | null; kategorie: string | null; created_at: string }
export type DienstleisterZeile = Awaited<ReturnType<typeof ladeDienstleisterFuerObjekt>>[number];
export interface TicketZeile { id: string; ticket_nummer: string | null; titel: string | null; status: string | null; prioritaet: string | null; created_at: string; faellig_am: string | null; einheit_id: string | null; mieter_id: string | null; eskaliert: boolean | null }
export interface VertragZeile { id: string; vertragsnummer: string | null; vertragsart: string | null; mieter_id: string | null; einheit: string | null; beginn: string | null; ende: string | null; unbefristet: boolean | null; kaltmiete: number | null; status: string | null; mietanpassung_typ: string | null; kuendigungsfrist_monate: number | null }
export interface UebergabeZeile { id: string; art: string; status: string; einheit_id: string | null; mieter_id: string | null; durchgefuehrt_am: string; schluessel: unknown }
export interface FachDokumentZeile extends DokumentZeile { mieter_id: string | null }
export interface DienstleisterLinkZeile { dienstleister_id: string; von: string | null; bis: string | null; vertragsart: string | null; aktiv: boolean | null }

/** Kategorien der Fachdokumente (Versicherung, Wartung) – wie im Dokumentenbereich. */
export const FACH_KATEGORIEN = ['Versicherung', 'Wartung'] as const;

const TICKET_SPALTEN = 'id, ticket_nummer, titel, status, prioritaet, created_at, faellig_am, einheit_id, mieter_id, eskaliert';
const VERTRAG_SPALTEN = 'id, vertragsnummer, vertragsart, mieter_id, einheit, beginn, ende, unbefristet, kaltmiete, status, mietanpassung_typ, kuendigungsfrist_monate';
const UEBERGABE_SPALTEN = 'id, art, status, einheit_id, mieter_id, durchgefuehrt_am, schluessel';
const DOK_SPALTEN = 'id, titel, datei_name, datei_typ, datei_url, onedrive_url, gdrive_file_id, kategorie, created_at, mieter_id';

/** Gemeinsame Lader für Objekt- und Einheit-Seite (E8). */
export const zusatzLader = {
  tickets: async (objektId: string) => daten(await supabase.from('tickets').select(TICKET_SPALTEN)
    .eq('objekt_id', objektId).is('geloescht_am', null).order('created_at', { ascending: false }).limit(200)) as TicketZeile[],
  vertraege: async (objektId: string) => daten(await supabase.from('vertraege').select(VERTRAG_SPALTEN)
    .eq('objekt_id', objektId).order('beginn', { ascending: false })) as VertragZeile[],
  uebergaben: async (objektId: string) => daten(await supabase.from('uebergabeprotokolle').select(UEBERGABE_SPALTEN)
    .eq('objekt_id', objektId).order('durchgefuehrt_am', { ascending: false })) as UebergabeZeile[],
  fachDokumente: async (objektId: string) => daten(await supabase.from('dokumente').select(DOK_SPALTEN)
    .eq('objekt_id', objektId).eq('is_deleted', false).in('kategorie', [...FACH_KATEGORIEN])
    .order('created_at', { ascending: false }).limit(100)) as FachDokumentZeile[],
  dienstleisterLinks: async (objektId: string) => daten(await supabase.from('dienstleister_objekt')
    .select('dienstleister_id, von, bis, vertragsart, aktiv').eq('objekt_id', objektId)) as DienstleisterLinkZeile[],
};

export interface ObjektDetailDaten {
  objekt: ObjektRoh;
  einheiten: EinheitDetailRoh[];
  mieter: MieterDetailRoh[];
  haeuser: HausRoh[];
  eigentuemer: EigentuemerZeile[];
  weg: WegZeile[];
  notizen: NotizZeile[];
  aliase: AliasZeile[];
  termine: TerminZeile[];
  zaehler: ZaehlerZeile[];
  dokumente: DokumentZeile[];
  dokumenteAnzahl: number;
  kontakte: ObjektKontaktZeile[];
  dienstleister: DienstleisterZeile[];
  zustaendigOptionen: ZustaendigOption[];
  tickets: TicketZeile[];
  vertraege: VertragZeile[];
  uebergaben: UebergabeZeile[];
  fachDokumente: FachDokumentZeile[];
  dienstleisterLinks: DienstleisterLinkZeile[];
}

export type Bereich = Exclude<keyof ObjektDetailDaten, 'objekt'>;

type Ergebnis<T> = { ok: true; wert: T } | { ok: false; fehler: string };

async function versuche<T>(f: () => PromiseLike<T> | T): Promise<Ergebnis<T>> {
  try { return { ok: true, wert: await f() }; } catch (e) { return { ok: false, fehler: e instanceof Error ? e.message : 'Unbekannter Fehler' }; }
}

function daten<T>(r: { data: T | null; error: { message: string } | null }): T {
  if (r.error) throw new Error(r.error.message);
  return (r.data ?? []) as T;
}

const LADER: { [K in Bereich]: (objektId: string, einheitIds: string[]) => Promise<ObjektDetailDaten[K]> } = {
  einheiten: async id => daten(await supabase.from('objekt_einheiten')
    .select('id, bezeichnung, typ, wohnflaeche, nutzflaeche, zimmer, etage, haus_id, status, uebergeordnete_einheit_id').eq('objekt_id', id)) as EinheitDetailRoh[],
  mieter: async id => daten(await supabase.from('mieter')
    .select('id, einheit_id, einheit, anrede, vorname, nachname, firma_name, status, kaltmiete, nebenkosten_vorauszahlung, heizkosten, weitere_kosten, miet_historie, mietbeginn, mietende, email, telefon, mobil')
    .eq('objekt_id', id)) as MieterDetailRoh[],
  haeuser: async id => daten(await supabase.from('objekt_haeuser')
    .select('id, bezeichnung, strasse, hausnummer, plz, stadt').eq('objekt_id', id).order('sortierung', { ascending: true })) as HausRoh[],
  eigentuemer: async id => daten(await supabase.from('v_objekt_eigentuemer').select('*').eq('objekt_id', id)) as EigentuemerZeile[],
  weg: async (_id, einheitIds) => {
    if (einheitIds.length === 0) return [];
    type Roh = EinheitEigZeitraum & { einheit_id: string; eigentuemer_id: string | null; mea: number | null; hausgeld_vorschuss: number | null; ruecklage_vorschuss: number | null; sev_status: string | null; eigentuemer: { vorname: string | null; nachname: string | null; firma_name: string | null } | null };
    const roh = daten(await supabase.from('einheit_eigentuemer')
      .select('einheit_id, eigentuemer_id, mea, hausgeld_vorschuss, ruecklage_vorschuss, sev_status, vertrag_ab, vertrag_bis, eigentuemer:eigentuemer_id(vorname, nachname, firma_name)')
      .in('einheit_id', einheitIds)) as unknown as Roh[];
    // Historie je Einheit: je Einheit die laufende Hauptzeile (MEA/Hausgeld nicht doppelt zählen)
    return aktuelleJeEinheit(roh, heuteIso()).map(w => ({
      einheit_id: w.einheit_id, eigentuemer_id: w.eigentuemer_id, mea: w.mea, hausgeld_vorschuss: w.hausgeld_vorschuss, ruecklage_vorschuss: w.ruecklage_vorschuss, sev_status: w.sev_status,
      name: w.eigentuemer ? ((w.eigentuemer.firma_name ?? '').trim() || `${w.eigentuemer.vorname ?? ''} ${w.eigentuemer.nachname ?? ''}`.trim()) : '',
    }));
  },
  notizen: async id => daten(await supabase.from('notizen').select('id, text, created_at').eq('objekt_id', id).order('created_at', { ascending: false })) as NotizZeile[],
  aliase: async id => daten(await supabase.from('objekt_aliase').select('id, alias, quelle').eq('objekt_id', id).order('created_at')) as AliasZeile[],
  termine: async id => daten(await supabase.from('termine')
    // '*' wie der Kalender: nicht jede Spalte (z. B. einheit_id) existiert in jeder Umgebung
    .select('*').eq('objekt_id', id)
    .order('start_datum', { ascending: true })) as TerminZeile[],
  zaehler: async id => daten(await supabase.from('zaehler')
    .select('id, zaehler_nummer, typ, einheit, standort, einheit_bezeichnung, aktiv, haus_id').eq('objekt_id', id).order('typ')) as ZaehlerZeile[],
  dokumente: async id => daten(await supabase.from('dokumente')
    .select('id, titel, datei_name, datei_typ, datei_url, onedrive_url, gdrive_file_id, kategorie, created_at').eq('objekt_id', id).eq('is_deleted', false)
    .order('created_at', { ascending: false }).limit(50)) as DokumentZeile[],
  dokumenteAnzahl: async id => {
    const r = await supabase.from('dokumente').select('id', { count: 'exact', head: true }).eq('objekt_id', id).eq('is_deleted', false);
    if (r.error) throw new Error(r.error.message);
    return r.count ?? 0;
  },
  kontakte: async id => ladeObjektKontakte(id),
  tickets: async id => zusatzLader.tickets(id),
  vertraege: async id => zusatzLader.vertraege(id),
  uebergaben: async id => zusatzLader.uebergaben(id),
  fachDokumente: async id => zusatzLader.fachDokumente(id),
  dienstleisterLinks: async id => zusatzLader.dienstleisterLinks(id),
  dienstleister: async id => ladeDienstleisterFuerObjekt(id),
  zustaendigOptionen: async () => {
    const [p, t] = await Promise.all([
      supabase.from('profiles').select('id, full_name').or('role.eq.team,role.eq.admin').eq('zuweisbar', true).order('full_name'),
      supabase.from('teams').select('id, name').order('name'),
    ]);
    const profile = daten(p) as { id: string; full_name: string | null }[];
    const teams = daten(t) as { id: string; name: string }[];
    return [
      ...profile.filter(x => x.full_name).map(x => ({ id: x.id, name: x.full_name ?? '', typ: 'mitarbeiter' as const })),
      ...teams.map(x => ({ id: x.id, name: x.name, typ: 'team' as const })),
    ];
  },
};

/** Ein Bereich laden – ohne Typ-Verengung, weil die Bereiche unterschiedliche Typen haben. */
function ladeBereich(b: Bereich, objektId: string, einheitIds: string[]): Promise<unknown> {
  return (LADER[b] as (o: string, e: string[]) => Promise<unknown>)(objektId, einheitIds);
}

const LEER: Omit<ObjektDetailDaten, 'objekt'> = {
  einheiten: [], mieter: [], haeuser: [], eigentuemer: [], weg: [], notizen: [], aliase: [], termine: [], zaehler: [],
  dokumente: [], dokumenteAnzahl: 0, kontakte: [], dienstleister: [], zustaendigOptionen: [],
  tickets: [], vertraege: [], uebergaben: [], fachDokumente: [], dienstleisterLinks: [],
};

export type DetailZustand =
  | { status: 'laedt' }
  | { status: 'fehlt' }
  | { status: 'fehler'; meldung: string }
  | { status: 'ok'; daten: ObjektDetailDaten; fehler: Partial<Record<Bereich, string>> };

export function useObjektDetail(objektId: string): {
  zustand: DetailZustand;
  neuLaden: (...bereiche: (Bereich | 'objekt')[]) => void;
} {
  const [zustand, setZustand] = useState<DetailZustand>({ status: 'laedt' });
  const aktuell = useRef(objektId);

  const ladeAlles = useCallback(async (id: string) => {
    const obj = await supabase.from('objekte').select('*').eq('id', id).maybeSingle();
    if (aktuell.current !== id) return;
    if (obj.error) { setZustand({ status: 'fehler', meldung: obj.error.message }); return; }
    if (!obj.data) { setZustand({ status: 'fehlt' }); return; }
    const einheitenErg = await versuche(() => LADER.einheiten(id, []));
    const einheitIds = einheitenErg.ok ? einheitenErg.wert.map(e => e.id) : [];
    const bereiche = (Object.keys(LADER) as Bereich[]).filter(b => b !== 'einheiten');
    const ergebnisse = await Promise.all(bereiche.map(b => versuche(() => ladeBereich(b, id, einheitIds))));
    if (aktuell.current !== id) return;
    const d: ObjektDetailDaten = { ...LEER, objekt: obj.data as ObjektRoh };
    const fehler: Partial<Record<Bereich, string>> = {};
    if (einheitenErg.ok) d.einheiten = einheitenErg.wert; else fehler.einheiten = einheitenErg.fehler;
    bereiche.forEach((b, i) => {
      const e = ergebnisse[i];
      if (!e) return;
      if (e.ok) (d as unknown as Record<Bereich, unknown>)[b] = e.wert;
      else fehler[b] = e.fehler;
    });
    setZustand({ status: 'ok', daten: d, fehler });
  }, []);

  useEffect(() => {
    aktuell.current = objektId;
    setZustand({ status: 'laedt' });
    void ladeAlles(objektId);
  }, [objektId, ladeAlles]);

  const neuLaden = useCallback((...bereiche: (Bereich | 'objekt')[]) => {
    const id = aktuell.current;
    if (bereiche.length === 0) { void ladeAlles(id); return; }
    void (async () => {
      if (bereiche.includes('objekt')) {
        const obj = await supabase.from('objekte').select('*').eq('id', id).maybeSingle();
        if (aktuell.current === id && obj.data) {
          setZustand(z => (z.status === 'ok' ? { ...z, daten: { ...z.daten, objekt: obj.data as ObjektRoh } } : z));
        }
      }
      const echte = bereiche.filter((b): b is Bereich => b !== 'objekt');
      if (echte.length === 0) return;
      const einheitIds = zustand.status === 'ok' ? zustand.daten.einheiten.map(e => e.id) : [];
      const erg = await Promise.all(echte.map(b => versuche(() => ladeBereich(b, id, einheitIds))));
      if (aktuell.current !== id) return;
      setZustand(z => {
        if (z.status !== 'ok') return z;
        const d = { ...z.daten } as unknown as Record<Bereich, unknown> & ObjektDetailDaten;
        const fehler = { ...z.fehler };
        echte.forEach((b, i) => {
          const e = erg[i];
          if (!e) return;
          if (e.ok) { d[b] = e.wert as never; delete fehler[b]; } else fehler[b] = e.fehler;
        });
        return { status: 'ok', daten: d, fehler };
      });
    })();
  }, [ladeAlles, zustand]);

  return { zustand, neuLaden };
}
